/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import { FC, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { makeSelectPageNewsTranslation } from '@/main/modules/internationalization/intSelectors';
import { modalOpen } from '@/main/modules/modal/modalActions';
import { initialise, Table, useTableInitialise, useTableListApi, useTableMetaApi } from '@modules/table';
import { tableIsErrorSelector, tableIsLoadingSelector } from '@modules/table/selectors';

import { NEWS_TABLE_CONFIG } from './constants';
import { useTableNewsConfig } from './hooks/useTableNewsColumns';
import { NewsTableDeleteModal } from './NewsTableDeleteModal';

const NewsTable: FC = () => {
    const dispatch = useDispatch();

    const pageTranslation = useSelector(makeSelectPageNewsTranslation());

    const { updatedConfig } = useTableInitialise(NEWS_TABLE_CONFIG);

    const handleDeleteItem = useCallback(
        (newsId: string, newsTitle: string) => {
            dispatch(
                modalOpen({
                    modalParams: {
                        isCustomCrossButton: true,
                    },
                    render: ({ onCloseModal }) => (
                        <NewsTableDeleteModal newsId={newsId} newsTitle={newsTitle} onCloseModal={onCloseModal} />
                    ),
                }),
            );
        },
        [dispatch],
    );

    /* Инициализация таблицы */
    useEffect(() => {
        if (updatedConfig) {
            dispatch(initialise(updatedConfig));
        }
    }, [dispatch, updatedConfig]);

    /* Table Meta  */
    const config = useTableNewsConfig(pageTranslation, handleDeleteItem);
    const { columns } = useTableMetaApi(config);

    /* Table List */
    const { data, isCompact, infiniteLoader } = useTableListApi();

    // /* Table Statuses  */
    const isLoading = useSelector(tableIsLoadingSelector);
    const isError = useSelector(tableIsErrorSelector);

    return (
        <Table
            columns={columns}
            data={data}
            dataName="newsTable"
            dataNameRow="news.id"
            infiniteScroll={infiniteLoader}
            isCompact={isCompact}
            isError={isError}
            isLoading={isLoading}
        />
    );
};

export default NewsTable;
